/**
 * OrderDetailsPageModern — Feb 2026 (Modern Card Theme · Phase 2)
 *
 * Modern re-skin of the customer order-tracking surface. Rendered when
 * the user has `ui_theme = 'modern'` (routed via OrderDetailsPageShell).
 *
 * Behavior parity is 1:1 with the legacy OrderDetailsPage:
 *   • Same axios fetch + 30s fallback poll
 *   • Same SSE stream (order.status_updated, order.assigned,
 *     order.location_updated, order.items_updated)
 *   • Same Notification permission handshake + iOS PWA nudge
 *   • Same Mapbox ETA calculation on driver-location tick
 *   • Same cancel handler + optimistic status timeline
 *
 * Visual language:
 *   – Inter font, warm off-white canvas (var(--am-canvas))
 *   – White rounded cards (.am-card / .am-card-lg)
 *   – Emerald complete/current step; hairline pending
 *   – Gold chip for size, red for cancel / destructive
 *
 * All data-testids are preserved from the legacy page so end-to-end
 * tests continue to pass unchanged.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../../components/Navbar';
import { formatOrderStatus } from '../../utils/orderStatus';
import { NotificationService } from '../../utils/notifications';
import { getETAToAddress } from '../../utils/mapbox';
import { useSSE } from '../../hooks/useSSE';
import { usePushSubscribe } from '../../hooks/usePushSubscribe';
import { useFlag } from '../../context/FeatureFlagsContext';
import { logEvent } from '../../utils/eventsLogger';
import {
  ArrowLeft, Package, MapPin, Clock, CreditCard, Truck,
  CheckCircle, XCircle, Navigation, Bell, BellOff, RefreshCw,
  Route, Radio, ChevronLeft,
} from 'lucide-react';
import { OrderFeeRows, slotWhen } from '../../components/OrderSlotSummary';
import { ReviewItemTag } from '../../components/reviews/ReviewItemTag';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const ORDER_STEPS = [
  { key: 'pending', label: 'Pending', icon: Clock },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  { key: 'preparing', label: 'Preparing', icon: Package },
  { key: 'out_for_delivery', label: 'Out for delivery', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle },
];

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// ─── Modern shell ───────────────────────────────────────────────
function ModernShell({ children }) {
  return (
    <div
      className="am-modern-scope"
      style={{ minHeight: '100vh', background: 'var(--am-canvas)' }}
      data-testid="order-details-page"
    >
      <Navbar />
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px 96px' }}>
        {children}
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────
function StatusBadgePill({ status }) {
  const cfg = {
    pending: { bg: 'var(--am-gold-50)', color: 'var(--am-gold-700)', label: 'Pending' },
    confirmed: { bg: 'var(--am-emerald-50)', color: 'var(--am-emerald-700)', label: 'Confirmed' },
    preparing: { bg: 'var(--am-emerald-50)', color: 'var(--am-emerald-700)', label: 'Preparing' },
    out_for_delivery: { bg: 'var(--am-emerald-50)', color: 'var(--am-emerald-700)', label: 'Out for delivery' },
    delivered: { bg: 'var(--am-emerald-100)', color: 'var(--am-emerald-800)', label: 'Delivered' },
    cancelled: { bg: '#FEE2E2', color: '#B91C1C', label: 'Cancelled' },
  };
  const s = cfg[status] || cfg.pending;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center',
        padding: '4px 10px',
        borderRadius: 'var(--am-radius-pill)',
        background: s.bg, color: s.color,
        fontSize: 11, fontWeight: 700, letterSpacing: '.03em',
        textTransform: 'uppercase',
      }}
    >
      {formatOrderStatus(status) || s.label}
    </span>
  );
}

function StatusTimeline({ currentStatus }) {
  if (currentStatus === 'cancelled') return null;
  const currentIndex = ORDER_STEPS.findIndex((s) => s.key === currentStatus);

  return (
    <div className="am-card-lg" style={{ padding: 20, marginBottom: 16 }}>
      <div className="am-eyebrow" style={{ marginBottom: 16, color: 'var(--am-ink-3)' }}>
        Order status
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {ORDER_STEPS.map((step, index) => {
          const Icon = step.icon;
          const isCompleted = index <= currentIndex;
          const isCurrent = index === currentIndex;
          return (
            <div
              key={step.key}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', position: 'relative',
              }}
            >
              {index > 0 && (
                <div
                  style={{
                    position: 'absolute', left: 0, right: '50%',
                    top: 14, height: 2,
                    background: index <= currentIndex ? 'var(--am-emerald-500)' : 'var(--am-hair)',
                  }}
                />
              )}
              {index < ORDER_STEPS.length - 1 && (
                <div
                  style={{
                    position: 'absolute', left: '50%', right: 0,
                    top: 14, height: 2,
                    background: index < currentIndex ? 'var(--am-emerald-500)' : 'var(--am-hair)',
                  }}
                />
              )}
              <div
                style={{
                  position: 'relative', zIndex: 1,
                  width: 30, height: 30, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isCompleted ? 'var(--am-emerald-600)' : 'var(--am-inset)',
                  color: isCompleted ? 'var(--am-ink-inv)' : 'var(--am-ink-3)',
                  boxShadow: isCurrent ? '0 0 0 4px var(--am-emerald-ring)' : 'none',
                  transition: 'all .2s ease',
                }}
              >
                <Icon size={14} />
              </div>
              <div
                style={{
                  marginTop: 8, fontSize: 11, fontWeight: 500,
                  color: isCompleted ? 'var(--am-ink-1)' : 'var(--am-ink-3)',
                  textAlign: 'center', letterSpacing: '.01em',
                }}
              >
                {step.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ETASection({ order, etaData, etaLoading, onRefresh, lastLocationUpdate, showDriverLocation }) {
  return (
    <div
      className="am-card-lg"
      style={{
        padding: 20, marginBottom: 16,
        borderColor: 'var(--am-emerald-500)',
        boxShadow: 'var(--am-shadow-2), 0 0 0 4px var(--am-emerald-ring)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Navigation
            size={18}
            color="var(--am-emerald-600)"
            style={{ animation: 'am-pulse 1.6s ease-in-out infinite' }}
          />
          <div>
            <div className="am-eyebrow" style={{ color: 'var(--am-emerald-700)' }}>
              Delivery in progress
            </div>
            <div style={{ color: 'var(--am-ink-3)', fontSize: 12 }}>
              Live updates every few seconds
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          style={{
            background: 'var(--am-inset)',
            border: '1px solid var(--am-hair)',
            borderRadius: 'var(--am-radius-pill)',
            padding: '6px 12px',
            fontSize: 12, fontWeight: 500,
            color: 'var(--am-ink-2)',
            cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}
          data-testid="refresh-location"
        >
          <RefreshCw size={12} className={etaLoading ? 'am-spin' : ''} />
          Refresh
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 12,
        }}
      >
        {/* ETA card */}
        <div
          className="am-card"
          style={{
            padding: 16, textAlign: 'center',
            background: 'var(--am-emerald-50)',
            borderColor: 'var(--am-emerald-200)',
          }}
        >
          <div className="am-eyebrow" style={{ color: 'var(--am-emerald-700)', marginBottom: 8 }}>
            Estimated arrival
          </div>
          <div
            className="am-tnum"
            style={{
              fontSize: 32, fontWeight: 700, lineHeight: 1.1,
              color: 'var(--am-emerald-700)', letterSpacing: '-0.02em',
            }}
          >
            {etaLoading ? (
              <span style={{ fontSize: 18 }}>Calculating…</span>
            ) : etaData ? (
              etaData.durationMinutes <= 1 ? 'Arriving' : `${etaData.durationMinutes} min`
            ) : (
              '– min'
            )}
          </div>
          <div style={{ color: 'var(--am-ink-3)', fontSize: 11, marginTop: 6 }}>
            {etaData ? (
              <>
                <Route size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {etaData.distanceText} away
              </>
            ) : (
              'Calculating route…'
            )}
          </div>
        </div>

        {/* Driver card */}
        <div className="am-card" style={{ padding: 16 }}>
          <div className="am-eyebrow" style={{ color: 'var(--am-ink-3)', marginBottom: 8 }}>
            Your driver
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'var(--am-emerald-100)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Truck size={16} color="var(--am-emerald-700)" />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--am-ink-1)' }}>
                {order.driver_name || 'Assigned soon'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--am-ink-3)' }}>
                En route to you
              </div>
            </div>
          </div>
        </div>

        {/* Steps card */}
        <div className="am-card" style={{ padding: 16 }}>
          <div className="am-eyebrow" style={{ color: 'var(--am-ink-3)', marginBottom: 8 }}>
            Delivery status
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <StepRow done label="Order confirmed" />
            <StepRow done label="Picked up by driver" />
            <StepRow active label="On the way" />
          </div>
        </div>
      </div>

      {showDriverLocation && order.driver_location && (
        <div
          className="am-card"
          style={{
            marginTop: 12, padding: 12,
            background: 'var(--am-inset)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--am-ink-2)' }}>
            <MapPin size={12} color="var(--am-emerald-600)" />
            <span className="am-tnum">
              Driver location:{' '}
              {Number(order.driver_location.lat ?? 0).toFixed(5)},{' '}
              {Number(order.driver_location.lng ?? 0).toFixed(5)}
            </span>
          </div>
          {lastLocationUpdate && (
            <span style={{ fontSize: 11, color: 'var(--am-ink-3)' }}>
              Updated {lastLocationUpdate.toLocaleTimeString()}
            </span>
          )}
        </div>
      )}
      {showDriverLocation && !order.driver_location && (
        <div
          className="am-card"
          style={{
            marginTop: 12, padding: 12,
            background: 'var(--am-gold-50)',
            borderColor: 'var(--am-gold-300)',
            color: 'var(--am-gold-700)',
            fontSize: 12,
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <Clock size={12} />
          Waiting for driver to share location…
        </div>
      )}
    </div>
  );
}

function StepRow({ done, active, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {done && !active && (
        <CheckCircle size={14} color="var(--am-emerald-600)" />
      )}
      {active && (
        <div
          style={{
            width: 14, height: 14, borderRadius: '50%',
            border: '2px solid var(--am-emerald-600)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: 5, height: 5, borderRadius: '50%',
              background: 'var(--am-emerald-600)',
              animation: 'am-pulse 1.6s ease-in-out infinite',
            }}
          />
        </div>
      )}
      <span
        style={{
          fontSize: 12,
          color: active ? 'var(--am-emerald-700)' : 'var(--am-ink-2)',
          fontWeight: active ? 600 : 400,
        }}
      >
        {label}
      </span>
    </div>
  );
}

function InfoCard({ Icon, title, children }) {
  return (
    <div className="am-card" style={{ padding: 16 }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          marginBottom: 8, color: 'var(--am-ink-3)',
        }}
      >
        <Icon size={14} />
        <span
          style={{
            fontSize: 11, fontWeight: 600, letterSpacing: '.06em',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </span>
      </div>
      <div style={{ color: 'var(--am-ink-1)', fontSize: 14 }}>{children}</div>
    </div>
  );
}

// ─── main ──────────────────────────────────────────────────────
export function OrderDetailsPageModern() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifStatus, setNotifStatus] = useState(() => NotificationService.getPermissionStatus());
  const [etaData, setEtaData] = useState(null);
  const [etaLoading, setEtaLoading] = useState(false);
  const [lastLocationUpdate, setLastLocationUpdate] = useState(null);
  const [sseConnected, setSseConnected] = useState(false);
  const showDriverLocation = useFlag('customer.driver_location', true);
  const { ensureSubscribed: ensurePushSubscribed } = usePushSubscribe();
  const previousStatusRef = useRef(null);
  const lastDriverLocationRef = useRef(null);

  const calculateETA = useCallback(async (driverLocation, deliveryAddress) => {
    if (!driverLocation || !deliveryAddress) return;
    const locKey = `${Number(driverLocation.lat ?? 0).toFixed(4)},${Number(driverLocation.lng ?? 0).toFixed(4)}`;
    if (lastDriverLocationRef.current === locKey) return;
    lastDriverLocationRef.current = locKey;
    setEtaLoading(true);
    try {
      const eta = await getETAToAddress(driverLocation, deliveryAddress);
      if (eta) setEtaData(eta);
    } catch (err) {
      console.error('ETA calculation error:', err);
    } finally {
      setEtaLoading(false);
    }
  }, []);

  const fetchOrder = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/orders/${orderId}`, { withCredentials: true });
      logEvent('order.tracked_viewed', {
        order_id: data.id,
        status: data.status,
      }, { dedupeKey: data.id });
      if (previousStatusRef.current && previousStatusRef.current !== data.status) {
        NotificationService.notifyOrderStatus(data.id, data.status, data.driver_name);
        toast.success(`Order status updated: ${formatOrderStatus(data.status)}`);
      }
      previousStatusRef.current = data.status;
      if (data.driver_location) {
        setLastLocationUpdate(new Date());
        if (data.status === 'out_for_delivery') {
          calculateETA(data.driver_location, data.delivery_address);
        }
      }
      setOrder(data);
    } catch {
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [orderId, calculateETA]);

  useEffect(() => {
    fetchOrder();
    const interval = setInterval(() => {
      if (order && !['delivered', 'cancelled'].includes(order.status)) {
        fetchOrder();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchOrder, order?.status]);

  const applyStatusChange = useCallback((newStatus) => {
    setOrder(prev => {
      if (!prev) return prev;
      if (prev.status === newStatus) return prev;
      NotificationService.notifyOrderStatus(prev.id, newStatus, prev.driver_name);
      toast.success(`Order status: ${newStatus.replace('_', ' ').toUpperCase()}`);
      return { ...prev, status: newStatus, updated_at: new Date().toISOString() };
    });
  }, []);

  const applyDriverAssigned = useCallback((driverId, driverName) => {
    setOrder(prev => {
      if (!prev) return prev;
      if (prev.driver_id === driverId) return prev;
      toast.info(`Driver assigned: ${driverName}`);
      return { ...prev, driver_id: driverId, driver_name: driverName };
    });
  }, []);

  const applyDriverLocation = useCallback((location) => {
    setLastLocationUpdate(new Date());
    setOrder(prev => {
      if (!prev) return prev;
      const nextOrder = { ...prev, driver_location: location };
      if (nextOrder.status === 'out_for_delivery') {
        calculateETA(location, nextOrder.delivery_address);
      }
      return nextOrder;
    });
  }, [calculateETA]);

  useSSE(useCallback((evt) => {
    if (!evt || !evt.type) return;
    setSseConnected(true);
    switch (evt.type) {
      case 'stream.ready':
        break;
      case 'order.status_updated':
        if (evt.order_id === orderId) applyStatusChange(evt.status);
        break;
      case 'order.assigned':
        if (evt.order_id === orderId) applyDriverAssigned(evt.driver_id, evt.driver_name);
        break;
      case 'order.location_updated':
        if (evt.order_id === orderId) applyDriverLocation(evt.location);
        break;
      case 'order.items_updated':
        if (evt.order_id === orderId) {
          setOrder(prev => prev ? { ...prev, items: evt.items, total: evt.total } : prev);
          toast.info('Your order was updated by the dispatcher');
        }
        break;
      default:
        break;
    }
  }, [orderId, applyStatusChange, applyDriverAssigned, applyDriverLocation]));

  useEffect(() => {
    if (order?.status !== 'out_for_delivery') {
      setEtaData(null);
      lastDriverLocationRef.current = null;
    }
  }, [order?.status]);

  useEffect(() => {
    if (notifStatus === 'granted') ensurePushSubscribed();
  }, [notifStatus, ensurePushSubscribed]);

  const handleEnableNotifications = async () => {
    const current = NotificationService.getPermissionStatus();
    if (current === 'ios-needs-pwa') {
      toast.info('Add to Home Screen for alerts', {
        description: 'Tap Share → "Add to Home Screen" to enable background notifications on iPhone. Live updates & sounds still work in this tab.',
        duration: 8000,
      });
      return;
    }
    if (current === 'unsupported') {
      toast.info('Notifications not supported in this browser', {
        description: 'Live updates still stream to this page — you\'ll see status changes in real time.',
        duration: 6000,
      });
      return;
    }
    if (current === 'denied') {
      toast.error('Notifications are blocked in browser settings', {
        description: 'Open this site\'s Settings (Aa icon in Safari → Website Settings, or browser lock icon → Site settings) to re-allow notifications.',
        duration: 9000,
      });
      return;
    }
    const granted = await NotificationService.requestPermission();
    setNotifStatus(NotificationService.getPermissionStatus());
    if (granted) {
      const pushOk = await ensurePushSubscribed();
      toast.success(
        pushOk
          ? 'Notifications enabled. You\'ll get pushes even when this tab is closed.'
          : 'Notifications enabled in this tab. (Background push could not register on this device.)'
      );
    } else {
      toast.error('Notifications permission not granted');
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    try {
      await axios.patch(`${API_URL}/api/orders/${orderId}/status`, { status: 'cancelled' }, { withCredentials: true });
      toast.success('Order cancelled');
      fetchOrder();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel order');
    }
  };

  // Loading state
  if (loading) {
    return (
      <ModernShell>
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '60vh', color: 'var(--am-ink-3)', fontSize: 14,
          }}
        >
          Loading order…
        </div>
      </ModernShell>
    );
  }

  // Not found
  if (!order) {
    return (
      <ModernShell>
        <div
          className="am-card-lg"
          style={{
            padding: '48px 24px', textAlign: 'center',
            maxWidth: 480, margin: '48px auto 0',
          }}
        >
          <div
            style={{
              width: 64, height: 64, borderRadius: '50%',
              background: '#FEE2E2',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <XCircle size={28} color="#B91C1C" />
          </div>
          <h1
            style={{
              fontSize: 20, fontWeight: 700,
              color: 'var(--am-ink-1)', margin: '0 0 8px',
            }}
          >
            Order not found
          </h1>
          <p style={{ color: 'var(--am-ink-3)', fontSize: 14, margin: '0 0 20px' }}>
            We couldn&apos;t find that order. It may have been removed.
          </p>
          <Link to="/customer/orders" style={{ textDecoration: 'none' }}>
            <button
              className="am-cta"
              style={{ padding: '10px 20px', fontSize: 14 }}
              data-testid="back-to-orders-notfound"
            >
              <ArrowLeft size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Back to orders
            </button>
          </Link>
        </div>
      </ModernShell>
    );
  }

  const active = !['delivered', 'cancelled'].includes(order.status);

  // Notification button config
  let notifLabel = 'Enable alerts';
  let NotifIcon = BellOff;
  let notifColor = 'var(--am-gold-700)';
  let notifBg = 'var(--am-gold-50)';
  let notifBorder = 'var(--am-gold-300)';
  if (notifStatus === 'granted') {
    notifLabel = 'Alerts on';
    NotifIcon = Bell;
    notifColor = 'var(--am-emerald-700)';
    notifBg = 'var(--am-emerald-50)';
    notifBorder = 'var(--am-emerald-500)';
  } else if (notifStatus === 'denied') {
    notifLabel = 'Alerts blocked';
    NotifIcon = BellOff;
    notifColor = '#B91C1C';
    notifBg = '#FEE2E2';
    notifBorder = '#FCA5A5';
  } else if (notifStatus === 'ios-needs-pwa') {
    notifLabel = 'Add to home screen';
    NotifIcon = BellOff;
    notifColor = 'var(--am-emerald-700)';
    notifBg = 'var(--am-emerald-50)';
    notifBorder = 'var(--am-emerald-200)';
  } else if (notifStatus === 'unsupported') {
    notifLabel = 'Alerts n/a';
    NotifIcon = BellOff;
    notifColor = 'var(--am-ink-3)';
    notifBg = 'var(--am-inset)';
    notifBorder = 'var(--am-hair)';
  }

  return (
    <ModernShell>
      {/* Header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          marginBottom: 20, flexWrap: 'wrap',
        }}
      >
        <Link
          to="/customer/orders"
          className="am-chip"
          style={{ textDecoration: 'none', fontWeight: 500, cursor: 'pointer' }}
          data-testid="back-to-orders"
        >
          <ChevronLeft size={14} />
          Orders
        </Link>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h1
            className="am-tnum"
            style={{
              margin: 0, fontSize: 22, fontWeight: 700,
              letterSpacing: '-0.02em', color: 'var(--am-ink-1)',
            }}
          >
            Order #{order.id.slice(0, 8).toUpperCase()}
          </h1>
          <div style={{ fontSize: 12, color: 'var(--am-ink-3)', marginTop: 2 }}>
            {new Date(order.created_at).toLocaleString()}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {active && (
            <span
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '4px 10px',
                borderRadius: 'var(--am-radius-pill)',
                background: sseConnected ? 'var(--am-emerald-50)' : 'var(--am-inset)',
                color: sseConnected ? 'var(--am-emerald-700)' : 'var(--am-ink-3)',
                border: `1px solid ${sseConnected ? 'var(--am-emerald-200)' : 'var(--am-hair)'}`,
                fontSize: 10, fontWeight: 700, letterSpacing: '.06em',
                textTransform: 'uppercase',
              }}
              data-testid="customer-sse-status"
              title={sseConnected ? 'Live updates connected' : 'Connecting…'}
            >
              <Radio
                size={10}
                style={{ animation: sseConnected ? 'am-pulse 1.6s ease-in-out infinite' : 'none' }}
              />
              {sseConnected ? 'Live' : '...'}
            </span>
          )}
          {active && (
            <button
              type="button"
              onClick={handleEnableNotifications}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '6px 12px',
                border: `1px solid ${notifBorder}`,
                background: notifBg, color: notifColor,
                borderRadius: 'var(--am-radius-pill)',
                fontSize: 11, fontWeight: 600, letterSpacing: '.03em',
                cursor: 'pointer',
              }}
              data-testid="notification-toggle"
              title={notifStatus === 'ios-needs-pwa'
                ? 'iOS Safari only supports notifications in Home Screen PWAs.'
                : notifLabel}
            >
              <NotifIcon size={12} />
              {notifLabel}
            </button>
          )}
          <StatusBadgePill status={order.status} />
        </div>
      </div>

      {/* Timeline */}
      <StatusTimeline currentStatus={order.status} />

      {/* ETA (Out for delivery) */}
      {order.status === 'out_for_delivery' && (
        <ETASection
          order={order}
          etaData={etaData}
          etaLoading={etaLoading}
          onRefresh={fetchOrder}
          lastLocationUpdate={lastLocationUpdate}
          showDriverLocation={showDriverLocation}
        />
      )}

      {/* Body grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 16,
        }}
      >
        {/* Items */}
        <div className="am-card-lg" style={{ padding: 20 }}>
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              marginBottom: 12, color: 'var(--am-ink-3)',
            }}
          >
            <Package size={14} />
            <span
              style={{
                fontSize: 11, fontWeight: 600, letterSpacing: '.06em',
                textTransform: 'uppercase',
              }}
            >
              Order items
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {order.items.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', padding: '10px 0',
                  borderBottom: index === order.items.length - 1
                    ? 'none'
                    : '1px solid var(--am-hair)',
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      color: 'var(--am-ink-1)', fontSize: 14, fontWeight: 500,
                    }}
                    data-testid={`order-detail-item-${index}`}
                  >
                    <span
                      style={{
                        whiteSpace: 'nowrap', overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {item.product_name}
                    </span>
                    {item.size && (
                      <span
                        className="am-chip am-chip-gold"
                        style={{ fontSize: 10, padding: '2px 6px', flexShrink: 0 }}
                        data-testid={`order-detail-item-size-${index}`}
                      >
                        {item.size}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--am-ink-3)', marginTop: 2 }}>
                    Qty: {item.quantity}
                  </div>
                  <div style={{ marginTop: 6 }}><ReviewItemTag order={order} productId={item.product_id} index={index} modern /></div>
                </div>
                <div
                  className="am-tnum"
                  style={{ fontSize: 14, fontWeight: 500, color: 'var(--am-ink-1)' }}
                >
                  {money(item.price * item.quantity)}
                </div>
              </div>
            ))}
            <OrderFeeRows order={order} modern />
            <div
              style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'baseline',
                paddingTop: 14, marginTop: 4,
                borderTop: '2px solid var(--am-hair-strong)',
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--am-ink-2)' }}>
                Total
              </span>
              <span
                className="am-tnum"
                style={{
                  fontSize: 20, fontWeight: 700,
                  color: 'var(--am-ink-1)', letterSpacing: '-0.01em',
                }}
              >
                {money(order.total)}
              </span>
            </div>
          </div>
        </div>

        {/* Delivery details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <InfoCard Icon={MapPin} title="Delivery address">
            {order.delivery_address}
          </InfoCard>
          {order.delivery_slot && (
            <InfoCard Icon={Clock} title="Delivery time">
              <span data-testid="order-delivery-time">{slotWhen(order.delivery_slot)}</span>
            </InfoCard>
          )}
          <InfoCard Icon={CreditCard} title="Payment method">
            <span style={{ textTransform: 'capitalize' }}>
              {(order.payment_method || '').replace(/_/g, ' ')}
            </span>
          </InfoCard>
          {order.driver_name && (
            <InfoCard Icon={Truck} title="Driver">
              {order.driver_name}
            </InfoCard>
          )}
          {order.notes && (
            <InfoCard Icon={Package} title="Order notes">
              {order.notes}
            </InfoCard>
          )}
        </div>
      </div>

      {/* Cancel */}
      {order.status === 'pending' && (
        <div style={{ marginTop: 20 }}>
          <button
            onClick={handleCancelOrder}
            style={{
              background: 'transparent',
              border: '1px solid #FCA5A5',
              color: '#B91C1C',
              borderRadius: 'var(--am-radius-pill)',
              padding: '10px 20px',
              fontSize: 13, fontWeight: 500,
              cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
            data-testid="cancel-order"
          >
            <XCircle size={14} />
            Cancel order
          </button>
        </div>
      )}
    </ModernShell>
  );
}

export default OrderDetailsPageModern;
